import {
  createColumn,
  formatAggregatedFooter,
  formatNumericCell,
  formatPriceCell,
  getCommonBodyCellProps,
  getCommonFooterCellProps,
  getCommonHeadCellProps,
} from "./commonUtils";
import { formatCurrency, formatNumber } from "../../../utils/dataCalculations";

export const getUntaggedPositionsColumns = (theme, globalFilter) => {
  const commonHeadCellProps = getCommonHeadCellProps(theme);
  const commonBodyCellProps = getCommonBodyCellProps(theme);
  const commonFooterCellProps = getCommonFooterCellProps(theme);

  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    const textStr = String(text);
    const searchStr = String(searchTerm);
    const regex = new RegExp(`(${searchStr})`, "gi");
    const parts = textStr.split(regex);
    return parts?.map((part, index) =>
      part.toLowerCase() === searchStr.toLowerCase() ? (
        <span
          key={index}
          style={{
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.contrastText,
            padding: "1px 2px",
            borderRadius: theme.shape.borderRadius * 0.5,
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  return [
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyType",
      "Strategy Type",
      150,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell }) =>
            highlightMatch(cell.getValue() || "-", globalFilter),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyName",
      "Strategy Name",
      200,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell }) =>
            highlightMatch(cell.getValue() || "-", globalFilter),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "security_name",
      "Security Name",
      180,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell }) =>
            highlightMatch(cell.getValue() || "-", globalFilter),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "trading_pnl",
      "Trading P&L",
      80,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "trading_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "current_price",
      "Current Price",
      85,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "realtime_quantity",
      "Real-time Qty",
      100,
      "right",
      {
        head: { borderLeft: `2px solid ${theme.palette.divider}` },
        body: { borderLeft: `2px solid ${theme.palette.divider}` },
        footer: { borderLeft: `2px solid ${theme.palette.divider}` },
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "net_trading_quantity",
      "Net Trade Today",
      100,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sod_quantity",
      "SOD Quantity",
      100,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB, columnId) => {
            const aValue = rowA.getValue(columnId);
            const bValue = rowB.getValue(columnId);
            if ((!aValue || aValue === "nan") && bValue && bValue !== "nan")
              return 1;
            if (aValue && aValue !== "nan" && (!bValue || bValue === "nan"))
              return -1;
            const a = !aValue || aValue === "nan" ? 0 : aValue;
            const b = !bValue || bValue === "nan" ? 0 : bValue;
            return a - b;
          },
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_quantity",
      "Buy Qty",
      100,
      "right",
      {
        head: { borderLeft: `2px solid ${theme.palette.divider}` },
        body: { borderLeft: `2px solid ${theme.palette.divider}` },
        footer: { borderLeft: `2px solid ${theme.palette.divider}` },
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_price",
      "Buy Price",
      80,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_MV",
      "Buy MV",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "buy_trade_MV",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_quantity",
      "Sell Qty",
      100,
      "right",
      {
        head: { borderLeft: `2px solid ${theme.palette.divider}` },
        body: { borderLeft: `2px solid ${theme.palette.divider}` },
        footer: { borderLeft: `2px solid ${theme.palette.divider}` },
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_price",
      "Sell Price",
      80,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_MV",
      "Sell MV",
      100,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "sell_trade_MV",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_quantity",
      "Short Qty",
      100,
      "right",
      {
        head: { borderLeft: `2px solid ${theme.palette.divider}` },
        body: { borderLeft: `2px solid ${theme.palette.divider}` },
        footer: { borderLeft: `2px solid ${theme.palette.divider}` },
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_price",
      "Short Price",
      80,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_MV",
      "Short MV",
      100,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "short_trade_MV",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_quantity",
      "Cover Qty",
      80,
      "right",
      {
        head: { borderLeft: `2px solid ${theme.palette.divider}` },
        body: { borderLeft: `2px solid ${theme.palette.divider}` },
        footer: { borderLeft: `2px solid ${theme.palette.divider}` },
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_price",
      "Cover Price",
      100,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_MV",
      "Cover MV",
      100,
      "right",
      {
        head: { borderRight: `2px solid ${theme.palette.divider}` },
        body: { borderRight: `2px solid ${theme.palette.divider}` },
        footer: { borderRight: `2px solid ${theme.palette.divider}` },
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "cover_trade_MV",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
        },
      },
    ),
  ];
};
